import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Science as ScienceIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  FolderOpen as FolderOpenIcon,
  Save as SaveIcon,
  DeleteOutline as DeleteOutlineIcon,
  FactCheck as FactCheckIcon,
  ViewList as ViewListIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import { WAIT_STEP_STATUS } from '../Step00_Wait/Wait_Schema';
import EtcInfoModal from './modals/EtcInfoModal';

/** 선택 공정 → 기타 안전·보건상 정보 모달 폼 (RIS_BASIC_01 load 로직과 동일) */
const BuildEtcFormDataFromProcess = (processData) => {
  if (!processData) return {};
  const transportMethod = processData.transportMethod || '';
  const transportMethodTxt = processData.transportMethodTxt || '';
  const workerTrait = processData.workerTrait || '';
  const workerTraitArray = workerTrait
    ? workerTrait.split(',').map((t) => t.trim())
    : [];

  return {
    accidentCnt: processData.accidentCnt || '',
    nearMissCnt: processData.nearMissCnt || '',
    handlingWeight: processData.handlingWeight || '',
    handlingForm: processData.handlingForm || '',
    transportMethod,
    transportMethod기계: transportMethod === '기계' ? transportMethodTxt : '',
    transportMethod인력: transportMethod === '인력' ? transportMethodTxt : '',
    isSafetyWorkPermit: processData.isSafetyWorkPermit || 'N',
    isEnvironmentMeasurement: processData.isEnvironmentMeasurement || null,
    environmentMeasurementValue: processData.environmentMeasurement || '',
    environmentMeasurementStandard: processData.exposureStandards || '',
    isSpecialSafetyTraining: processData.isSpecialSafetyTraining || null,
    shiftType: processData.shiftType || 'N',
    shiftCount: processData.shiftTypeTxt || '',
    workerTrait,
    femaleWorker: workerTraitArray.includes('여성근로자'),
    elderlyWorker: workerTraitArray.includes('고령근로자'),
    under1YearWorker: workerTraitArray.includes('1년미만미숙련자'),
    irregularWorker: workerTraitArray.includes('비정규직근로자'),
    foreignWorker: workerTraitArray.includes('외국인근로자'),
    disabledWorker: workerTraitArray.includes('장애근로자'),
  };
};

const ACCENT_GREEN = '#2CA900';
const HEADER_GREEN = '#2CA900';
const HEADER_GREEN_DARK = '#217F00';
const CARD_BORDER = 'rgba(0,0,0,0.08)';

/** 좌·우 패널 공통 카드 스타일 */
const SECTION_CARD_SX = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: CARD_BORDER,
  bgcolor: '#fff',
  boxShadow: '0 4px 22px rgba(16, 24, 40, 0.08)',
  overflow: 'hidden',
};

const SectionTitleIconBox = ({ children, bgcolor, color }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: 1.5,
      bgcolor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      '& .MuiSvgIcon-root': { color, fontSize: 22 },
    }}
  >
    {children}
  </Box>
);

const RS_GUBUN_OPTIONS = ['최초평가', '정기평가', '수시평가'];
const RS_METHOD_OPTIONS = [
  { value: '위험성 수준 3단계 판단법', label: '위험성 수준 3단계' },
  { value: '빈도·강도법', label: '빈도·강도법' },
];

const CURRENT_STEP_INDEX = 0;
const CURRENT_STEP_ID = 'safetyInfo';

/** 스텝 원(40px) 세로 중앙에 맞춘 연결선 위치 */
const STEPPER_RAIL_TOP_PX = 16;

/** 기본 평가 정보는 상위(실시규정 등)에서 확정된 값 — 이 화면에서는 조회만 */
const BASIC_EVAL_INFO_DISABLED = true;

/** 읽기 전용 영역 배경(카드·필드) — 입력 불가임을 시각적으로 구분 */
const BASIC_INFO_PANEL_BG = '#F0F2F5';
const BASIC_INFO_FIELD_BG = '#E8EAED';
const basicEvalReadOnlyFieldSx = BASIC_EVAL_INFO_DISABLED
  ? {
      '& .MuiOutlinedInput-root': {
        bgcolor: BASIC_INFO_FIELD_BG,
      },
      '& .MuiOutlinedInput-root.Mui-disabled': {
        bgcolor: `${BASIC_INFO_FIELD_BG} !important`,
      },
      '& .Mui-disabled': {
        WebkitTextFillColor: 'rgba(0,0,0,0.55)',
      },
    }
  : {};

function SafetyInfo({
  value,
  onChange,
  onCancel,
  onSave,
  onLoadExisting,
  onTempSave,
  onOpenProcessSelectModal,
  onOpenFacilitySelectModal,
  onOpenMaterialSelectModal,
  workflowSteps,
}) {
  const form = value ?? {};
  const processList = useMemo(
    () => (Array.isArray(form.processList) ? form.processList : []),
    [form.processList]
  );

  const [etcInfoModalOpen, setEtcInfoModalOpen] = useState(false);
  const [etcInfoForm, setEtcInfoForm] = useState({});
  const [selectedDeptName, setSelectedDeptName] = useState('');
  const [processDeleteConfirmOpen, setProcessDeleteConfirmOpen] =
    useState(false);
  const [pendingDeleteProcessNm, setPendingDeleteProcessNm] = useState('');

  const selectedProcess =
    processList.find((p) => p.selected) || processList[0] || null;
  const departmentList = useMemo(() => {
    const map = new Map();
    processList.forEach((p) => {
      const name = String(p?.depNm ?? form.depNm ?? '').trim() || '미지정';
      const prev = map.get(name) ?? 0;
      map.set(name, prev + 1);
    });
    return Array.from(map.entries()).map(([name, count]) => ({ name, count }));
  }, [processList, form.depNm]);
  const visibleProcessList = useMemo(() => {
    if (!selectedDeptName) return processList;
    return processList.filter((p) => {
      const depName = String(p?.depNm ?? form.depNm ?? '').trim() || '미지정';
      return depName === selectedDeptName;
    });
  }, [processList, selectedDeptName, form.depNm]);

  useEffect(() => {
    if (departmentList.length === 0) {
      if (selectedDeptName) setSelectedDeptName('');
      return;
    }
    const selectedProcessDept =
      String(selectedProcess?.depNm ?? form.depNm ?? '').trim() || '미지정';
    const hasCurrent = departmentList.some((d) => d.name === selectedDeptName);
    if (hasCurrent) return;
    setSelectedDeptName(
      selectedProcessDept ||
        departmentList[0]?.name ||
        String(form.depNm ?? '').trim() ||
        '미지정'
    );
  }, [departmentList, selectedDeptName, selectedProcess, form.depNm]);
  /** 공정별 설비·물질 우선 표시 (없으면 루트 폼 필드 호환) */
  const equipmentList = Array.isArray(selectedProcess?.equipmentList)
    ? selectedProcess.equipmentList
    : Array.isArray(form.equipmentList)
      ? form.equipmentList
      : [];
  const materialList = Array.isArray(selectedProcess?.materialList)
    ? selectedProcess.materialList
    : Array.isArray(form.materialList)
      ? form.materialList
      : [];
  const stepList = Array.isArray(workflowSteps) ? workflowSteps : [];
  const currentStepIndexFromId = stepList.findIndex(
    (s) => (s?.routeStepId || s?.stepId) === CURRENT_STEP_ID
  );
  const currentStepIndex =
    currentStepIndexFromId >= 0
      ? currentStepIndexFromId
      : stepList.length > 0
        ? Math.min(CURRENT_STEP_INDEX, stepList.length - 1)
        : -1;
  const nextStepTitle = stepList[currentStepIndex + 1]?.title ?? '다음 단계';

  const SelectProcess = (processId) => {
    const nextProcessList = processList.map((p) => ({
      ...p,
      selected: p.processId === processId,
    }));
    const nextSelected =
      nextProcessList.find((p) => p.selected) || nextProcessList[0] || null;
    onChange?.({
      processList: nextProcessList,
      equipmentList: Array.isArray(nextSelected?.equipmentList)
        ? nextSelected.equipmentList
        : [],
      materialList: Array.isArray(nextSelected?.materialList)
        ? nextSelected.materialList
        : [],
    });
  };

  /** 공정 삭제 확인 팝업 오픈 */
  const RemoveSelectedProcess = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!selectedProcess) return;
    setPendingDeleteProcessNm(
      String(selectedProcess.processNm ?? '').trim() || '선택 공정'
    );
    setProcessDeleteConfirmOpen(true);
  };

  const CloseProcessDeleteConfirm = () => {
    setProcessDeleteConfirmOpen(false);
    setPendingDeleteProcessNm('');
  };

  /** 확인 후 목록에서 제거 — 저장/임시저장 시 서버에서 공정+Step02 세부 함께 삭제 */
  const ConfirmRemoveSelectedProcess = () => {
    const targetNm = pendingDeleteProcessNm;
    CloseProcessDeleteConfirm();
    const removeIdx = processList.findIndex((p) => p.selected);
    const fallbackIdx =
      removeIdx >= 0
        ? removeIdx
        : processList.findIndex(
            (p) => String(p.processNm ?? '').trim() === targetNm
          );
    const idx = fallbackIdx >= 0 ? fallbackIdx : 0;
    if (!processList.length) return;
    const filtered = processList.filter((_, i) => i !== idx);
    if (filtered.length === 0) {
      onChange?.({
        processList: [],
        equipmentList: [],
        materialList: [],
      });
      return;
    }
    const nextSelIdx = Math.min(idx, filtered.length - 1);
    const reindexed = filtered.map((p, i) => ({
      ...p,
      processNo: i + 1,
      selected: i === nextSelIdx,
    }));
    const nextSelected = reindexed[nextSelIdx];
    onChange?.({
      processList: reindexed,
      equipmentList: Array.isArray(nextSelected?.equipmentList)
        ? nextSelected.equipmentList
        : [],
      materialList: Array.isArray(nextSelected?.materialList)
        ? nextSelected.materialList
        : [],
    });
  };

  const HandleProcessNmChange = (value) => {
    if (!selectedProcess) return;
    const nextNm = value;
    const nextProcessList = processList.map((p) =>
      p.selected ? { ...p, processNm: nextNm } : p
    );
    onChange?.({ processList: nextProcessList });
  };

  const OpenEtcInfoModal = () => {
    if (!selectedProcess) return;
    setEtcInfoForm(BuildEtcFormDataFromProcess(selectedProcess));
    setEtcInfoModalOpen(true);
  };

  const CloseEtcInfoModal = () => {
    setEtcInfoModalOpen(false);
  };

  const HandleEtcInfoFieldChange = (field, value) => {
    setEtcInfoForm((prev) => ({ ...prev, [field]: value }));
  };

  const HandleEtcInfoTransportMeansToggle = (means) => {
    setEtcInfoForm((prev) => {
      const currentMeans = prev.transportMethod || '';
      const newMeans = currentMeans === means ? '' : means;
      return { ...prev, transportMethod: newMeans };
    });
  };

  const HandleConfirmEtcInfo = () => {
    if (!selectedProcess?.processId) {
      setEtcInfoModalOpen(false);
      return;
    }
    const transportMethodTxt =
      etcInfoForm.transportMethod === '기계'
        ? etcInfoForm.transportMethod기계
        : etcInfoForm.transportMethod === '인력'
          ? etcInfoForm.transportMethod인력
          : null;
    const workerTrait = etcInfoForm.workerTrait || null;

    onChange?.({
      processList: processList.map((p) =>
        p.processId !== selectedProcess.processId
          ? p
          : {
              ...p,
              accidentCnt: etcInfoForm.accidentCnt || null,
              nearMissCnt: etcInfoForm.nearMissCnt || null,
              handlingWeight: etcInfoForm.handlingWeight || null,
              handlingForm: etcInfoForm.handlingForm || null,
              transportMethod: etcInfoForm.transportMethod || null,
              transportMethodTxt: transportMethodTxt || null,
              isSafetyWorkPermit: etcInfoForm.isSafetyWorkPermit || 'N',
              isEnvironmentMeasurement:
                etcInfoForm.isEnvironmentMeasurement || null,
              environmentMeasurement:
                etcInfoForm.environmentMeasurementValue || null,
              exposureStandards:
                etcInfoForm.environmentMeasurementStandard || null,
              isSpecialSafetyTraining:
                etcInfoForm.isSpecialSafetyTraining || null,
              shiftType: etcInfoForm.shiftType || 'N',
              shiftTypeTxt: etcInfoForm.shiftCount || null,
              workerTrait,
            }
      ),
    });
    setEtcInfoModalOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100dvh - 88px)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        bgcolor: '#f0f4f0',
      }}
    >
      {/* 상단 타이틀 바 */}
      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: HEADER_GREEN,
          color: '#fff',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ letterSpacing: -0.2 }}
          >
            위험성평가 안전보건정보 기입 및 수정
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FolderOpenIcon sx={{ fontSize: 18 }} />}
              onClick={onLoadExisting}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.55)',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              기존 위험성평가 불러오기
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
              onClick={onTempSave}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.55)',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              임시 저장
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* 스텝퍼 — API(GetRisCustom00R1)에서만 스텝 수·순서·제목 수신 */}
      {stepList.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: { xs: 1.5, sm: 3 },
            pt: 1.5,
            pb: 1.25,
            flexShrink: 0,
            overflowX: 'auto',
            bgcolor: '#FAFBFC',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minWidth: { xs: 640, sm: 'auto' },
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: { xs: '5%', sm: '6%' },
                right: { xs: '5%', sm: '6%' },
                top: STEPPER_RAIL_TOP_PX,
                height: 2,
                bgcolor: '#D6DCE4',
                borderRadius: 1,
                zIndex: 0,
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ position: 'relative', zIndex: 1, width: '100%' }}
            >
              {stepList.map((step, index) => {
                const isActive =
                  currentStepIndex >= 0 && index === currentStepIndex;
                const isDone =
                  step?.status === WAIT_STEP_STATUS.completed && !isActive;
                const isPast =
                  isDone || (currentStepIndex >= 0 && index < currentStepIndex);
                return (
                  <Stack
                    key={String(step.stepId ?? step.routeStepId ?? index)}
                    alignItems="center"
                    sx={{
                      flex: '1 1 0',
                      minWidth: 0,
                      px: { xs: 0.25, sm: 0.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        bgcolor: isActive
                          ? '#0F9D00'
                          : isPast
                            ? '#DDF3D7'
                            : '#F7F9FC',
                        color: isActive || isPast ? '#fff' : '#667085',
                        border: isActive
                          ? '2px solid #0A6B00'
                          : isPast
                            ? '2px solid #4FA73D'
                            : '2px solid #C6CED8',
                        boxShadow: isActive
                          ? '0 3px 12px rgba(15, 157, 0, 0.50)'
                          : isPast
                            ? '0 1px 6px rgba(79, 167, 61, 0.30)'
                            : '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="caption"
                      align="center"
                      sx={{
                        mt: 0.6,
                        fontWeight: isActive ? 800 : isPast ? 700 : 500,
                        color: isActive
                          ? '#0A6B00'
                          : isPast
                            ? '#267A1A'
                            : '#98A2B3',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: 108,
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      ) : null}

      {/* 본문 — 좌측(기본·공정) 넓게 / 우측 설비·물질 꽉 채움 */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1.5, sm: 2 },
          overflow: 'auto',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: '1 1 auto',
            borderRadius: 2,
            borderColor: CARD_BORDER,
            bgcolor: '#fff',
            boxShadow: '0 6px 18px rgba(16, 24, 40, 0.06)',
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ flex: '1 1 auto', minHeight: 0, alignItems: 'stretch' }}
          >
            <Box
              sx={{
                width: { xs: '100%', md: '42%' },
                minWidth: { md: 400 },
                maxWidth: { md: 600 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minHeight: 0,
                alignSelf: 'stretch',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  ...SECTION_CARD_SX,
                  p: 2,
                  flexShrink: 0,
                  ...(BASIC_EVAL_INFO_DISABLED && {
                    bgcolor: BASIC_INFO_PANEL_BG,
                    borderColor: 'rgba(0,0,0,0.07)',
                    boxShadow: 'none',
                  }),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'rgba(0,0,0,0.08)',
                  }}
                >
                  <SectionTitleIconBox
                    bgcolor="#E8F5E9"
                    color={HEADER_GREEN_DARK}
                  >
                    <FactCheckIcon />
                  </SectionTitleIconBox>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      기본 평가 정보
                    </Typography>
                    {BASIC_EVAL_INFO_DISABLED && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        등록된 평가 정보이며 이 단계에서는 수정할 수 없습니다.
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Stack spacing={1.25}>
                  <TextField
                    size="small"
                    label="위험성평가명"
                    value={form.rsNm ?? ''}
                    onChange={(e) => onChange?.({ rsNm: e.target.value })}
                    fullWidth
                    disabled={BASIC_EVAL_INFO_DISABLED}
                    sx={basicEvalReadOnlyFieldSx}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl
                      size="small"
                      fullWidth
                      disabled={BASIC_EVAL_INFO_DISABLED}
                      sx={basicEvalReadOnlyFieldSx}
                    >
                      <InputLabel>평가구분</InputLabel>
                      <Select
                        label="평가구분"
                        value={form.rsGubun ?? '최초평가'}
                        onChange={(e) =>
                          onChange?.({ rsGubun: e.target.value })
                        }
                      >
                        {RS_GUBUN_OPTIONS.map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      size="small"
                      fullWidth
                      disabled={BASIC_EVAL_INFO_DISABLED}
                      sx={basicEvalReadOnlyFieldSx}
                    >
                      <InputLabel>평가방법</InputLabel>
                      <Select
                        label="평가방법"
                        value={form.rsMethod ?? RS_METHOD_OPTIONS[0].value}
                        onChange={(e) =>
                          onChange?.({ rsMethod: e.target.value })
                        }
                      >
                        {RS_METHOD_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <TextField
                    size="small"
                    label="평가 부서"
                    value={form.depNm ?? ''}
                    onChange={(e) => onChange?.({ depNm: e.target.value })}
                    fullWidth
                    disabled={BASIC_EVAL_INFO_DISABLED}
                    sx={basicEvalReadOnlyFieldSx}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="시작일"
                      value={form.rsStartDate ?? ''}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) =>
                        onChange?.({ rsStartDate: e.target.value })
                      }
                      disabled={BASIC_EVAL_INFO_DISABLED}
                      sx={basicEvalReadOnlyFieldSx}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="종료일"
                      value={form.rsEndDate ?? ''}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) =>
                        onChange?.({ rsEndDate: e.target.value })
                      }
                      disabled={BASIC_EVAL_INFO_DISABLED}
                      sx={basicEvalReadOnlyFieldSx}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  ...SECTION_CARD_SX,
                  p: 2,
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 320, md: 460 },
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'rgba(0,0,0,0.06)',
                    flexShrink: 0,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <SectionTitleIconBox bgcolor="#E3F2FD" color="#1565C0">
                      <ViewListIcon />
                    </SectionTitleIconBox>
                    <Typography variant="subtitle1" fontWeight={800}>
                      공정 목록
                    </Typography>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      width: '38%',
                      minWidth: 140,
                      borderColor: 'rgba(0,0,0,0.08)',
                      borderRadius: 1.5,
                      overflowY: 'auto',
                      p: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 0.5, pb: 0.5, display: 'block' }}
                    >
                      부서
                    </Typography>
                    <Stack spacing={0.5}>
                      {departmentList.map((dept) => {
                        const isSelected = dept.name === selectedDeptName;
                        return (
                          <Box
                            key={dept.name}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedDeptName(dept.name)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedDeptName(dept.name);
                              }
                            }}
                            sx={{
                              px: 1,
                              py: 0.85,
                              borderRadius: 1.25,
                              border: '1px solid',
                              borderColor: isSelected
                                ? '#81C784'
                                : 'rgba(0,0,0,0.08)',
                              bgcolor: isSelected ? '#E8F5E9' : '#fff',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#A5D6A7' },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 700 : 500}
                              noWrap
                            >
                              {dept.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dept.count}건
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderColor: 'rgba(0,0,0,0.08)',
                      borderRadius: 1.5,
                      overflowY: 'auto',
                      p: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 0.5, pb: 0.5, display: 'block' }}
                    >
                      공정
                    </Typography>
                    <Stack spacing={0.5}>
                      {visibleProcessList.map((process) => (
                        <Box
                          key={process.processId}
                          role="button"
                          tabIndex={0}
                          onClick={() => SelectProcess(process.processId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              SelectProcess(process.processId);
                            }
                          }}
                          sx={{
                            px: 1.25,
                            py: 1,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: process.selected
                              ? '#81C784'
                              : 'rgba(0,0,0,0.08)',
                            bgcolor: process.selected ? '#E8F5E9' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            '&:hover': { borderColor: '#A5D6A7' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={process.selected ? 700 : 500}
                          >
                            {process.processNm}
                          </Typography>
                          <ChevronRightIcon
                            sx={{ color: 'text.secondary', fontSize: 20 }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => onOpenProcessSelectModal?.()}
                  sx={{
                    mt: 1.5,
                    flexShrink: 0,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderStyle: 'dashed',
                    borderColor: 'rgba(0,0,0,0.2)',
                    color: 'text.secondary',
                  }}
                >
                  신규 공정 추가하기
                </Button>
              </Paper>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: CARD_BORDER,
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minHeight: 0,
                  boxShadow: '0 4px 22px rgba(16, 24, 40, 0.08)',
                }}
              >
                <Box
                  sx={{
                    bgcolor: ACCENT_GREEN,
                    color: '#fff',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FactoryIcon sx={{ fontSize: 24, color: '#fff' }} />
                      </Box>
                      <Chip
                        label={selectedProcess?.processNo ?? 1}
                        size="small"
                        sx={{
                          bgcolor: HEADER_GREEN_DARK,
                          color: '#fff',
                          fontWeight: 800,
                        }}
                      />
                      <Chip
                        size="small"
                        label={selectedProcess?.depNm || form.depNm || '부서'}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                    <TextField
                      size="small"
                      label="공정명"
                      value={selectedProcess?.processNm ?? ''}
                      onChange={(e) => HandleProcessNmChange(e.target.value)}
                      disabled={!selectedProcess}
                      fullWidth
                      sx={{
                        mt: 1.25,
                        maxWidth: 420,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(255,255,255,0.95)',
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.95, display: 'block', mt: 0.75 }}
                    >
                      부서: {selectedProcess?.depNm || form.depNm || '—'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.6)',
                      }}
                      variant="outlined"
                      onClick={OpenEtcInfoModal}
                      disabled={!selectedProcess}
                    >
                      기타 정보 입력
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      sx={{ minWidth: 40, color: '#fff' }}
                      onClick={RemoveSelectedProcess}
                      disabled={!selectedProcess}
                      aria-label="선택 공정 삭제"
                    >
                      <DeleteOutlineIcon />
                    </Button>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#fafbfa',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    sx={{
                      flex: 1,
                      minHeight: { xs: 'auto', lg: 0 },
                      alignItems: 'stretch',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: { xs: 260, lg: 0 },
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        borderRadius: 2.5,
                        border: '1px solid #E5D9C8',
                        bgcolor: '#FFFCF7',
                        boxShadow: '0 6px 24px rgba(139, 90, 0, 0.08)',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{
                          mb: 1.25,
                          pb: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'rgba(184, 134, 11, 0.2)',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                        >
                          <SectionTitleIconBox
                            bgcolor="#FFF3E0"
                            color="#E65100"
                          >
                            <PrecisionManufacturingIcon />
                          </SectionTitleIconBox>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800}>
                              사용 설비
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              이 공정에서 사용되는 기계/기구
                            </Typography>
                          </Box>
                        </Stack>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => onOpenFacilitySelectModal?.()}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          추가
                        </Button>
                      </Stack>
                      <Stack
                        spacing={1.25}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflow: 'auto',
                          pr: 0.25,
                        }}
                      >
                        {equipmentList.map((item) => (
                          <Box
                            key={item.id || item.mapId || item.name}
                            sx={{
                              p: 1.35,
                              borderRadius: 1.5,
                              bgcolor: '#fff',
                              border: '1px solid #F0E4CF',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#FF9800',
                                flexShrink: 0,
                                boxShadow: '0 0 0 2px rgba(255,152,0,0.25)',
                              }}
                            />
                            <Typography variant="body2" fontWeight={700}>
                              {item.name}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: { xs: 260, lg: 0 },
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        borderRadius: 2.5,
                        border: '1px solid #C5D9F0',
                        bgcolor: '#F7FAFF',
                        boxShadow: '0 6px 24px rgba(63, 110, 179, 0.1)',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{
                          mb: 1.25,
                          pb: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'rgba(63, 110, 179, 0.18)',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                        >
                          <SectionTitleIconBox
                            bgcolor="#E3F2FD"
                            color="#1565C0"
                          >
                            <ScienceIcon />
                          </SectionTitleIconBox>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800}>
                              취급 물질
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              이 공정에서 취급되는 화학물질
                            </Typography>
                          </Box>
                        </Stack>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => onOpenMaterialSelectModal?.()}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          추가
                        </Button>
                      </Stack>
                      <Stack
                        spacing={1.25}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflow: 'auto',
                          pr: 0.25,
                        }}
                      >
                        {materialList.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              p: 1.35,
                              borderRadius: 1.5,
                              bgcolor: '#fff',
                              border: '1px solid #DFEAFB',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                            }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              CAS No. {item.casNo ? item.casNo : '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* 하단 푸터 */}
      <Paper
        elevation={0}
        square
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          flexShrink: 0,
          bgcolor: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              alignSelf: { xs: 'stretch', sm: 'auto' },
            }}
          >
            이전 단계
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              다음 단계: {nextStepTitle}
            </Typography>
            <Button
              variant="contained"
              onClick={onSave}
              endIcon={<ChevronRightIcon />}
              fullWidth={false}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 3,
                bgcolor: ACCENT_GREEN,
                '&:hover': { bgcolor: HEADER_GREEN_DARK },
                alignSelf: { xs: 'stretch', sm: 'auto' },
              }}
            >
              저장 후 다음
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <EtcInfoModal
        open={etcInfoModalOpen}
        onClose={CloseEtcInfoModal}
        onSave={HandleConfirmEtcInfo}
        form={etcInfoForm}
        onFieldChange={HandleEtcInfoFieldChange}
        onTransportMeansToggle={HandleEtcInfoTransportMeansToggle}
      />

      <MuiDialog
        open={processDeleteConfirmOpen}
        onClose={CloseProcessDeleteConfirm}
        fullWidth
        maxWidth="xs"
        aria-labelledby="process-delete-dialog-title"
      >
        <DialogTitle id="process-delete-dialog-title">공정 삭제</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body1" fontWeight={700}>
              「{pendingDeleteProcessNm || '선택 공정'}」 공정을 삭제하시겠습니까?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              임시저장·저장 시 이 평가에서 해당 공정이 제거되며, 위험성평가
              실시(Step02)에 등록된 세부항목도 함께 삭제됩니다.
            </Typography>
            <Typography variant="body2" color="error.main" fontWeight={600}>
              삭제된 세부항목은 복구할 수 없습니다.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={CloseProcessDeleteConfirm} color="inherit">
            취소
          </Button>
          <Button
            onClick={ConfirmRemoveSelectedProcess}
            color="error"
            variant="contained"
          >
            삭제
          </Button>
        </DialogActions>
      </MuiDialog>
    </Box>
  );
}

export default SafetyInfo;
